import { mediaQueryLarge, requestIdleCallback, startViewTransition } from "@theme/utilities"
import { ThemeEvents } from "@theme/events"
import PaginatedList from "@theme/paginated-list"

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback()

    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange)
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate)
    this.#sortByQuantity()
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener("change", this.#handleMediaQueryChange)
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate)
  }

  /**
   * Re-sorts after filter/sort changes once the section has been re-rendered (morphed).
   */
  #handleFilterUpdate = () => {
    const observer = new MutationObserver(() => {
      observer.disconnect()
      this.#sortByQuantity()
    })
    observer.observe(this, { childList: true, subtree: true })
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return

    this.#animateLayoutChange(target.value)
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs

    if (!grid) return

    await startViewTransition(() => this.#setLayout(value), ["product-grid"])

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? "desktop" : "mobile"
      sessionStorage.setItem(`product-grid-view-${viewport}`, value)
    })
  }

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs
    if (!grid) return
    grid.setAttribute("product-grid-view", value)
  }

  /**
   * Sorts product grid items into three groups, each sorted by quantity (descending):
   * 1. Exact matches – title contains the search terms in exact order
   * 2. High relevance – top 50% of remaining items by Shopify relevance
   * 3. Low relevance – bottom 50% of remaining items
   */
  #sortByQuantity() {
    const grid = this.querySelector('[ref="grid"]')
    if (!grid) return

    const items = /** @type {HTMLElement[]} */ ([...grid.querySelectorAll("[data-quantity][data-relevance]")])
    if (items.length === 0) return

    const searchTerms = (this.dataset.searchTerms || "").trim()

    const sortByQtyDesc = (a, b) => {
      const qtyA = parseInt(a.dataset.quantity, 10) || 0
      const qtyB = parseInt(b.dataset.quantity, 10) || 0
      return qtyB - qtyA
    }

    const exactMatches = []
    const rest = []

    for (const item of items) {
      if (searchTerms && item.dataset.title?.includes(searchTerms)) {
        exactMatches.push(item)
      } else {
        rest.push(item)
      }
    }

    const threshold = Math.ceil(rest.length * 0.5)

    const highRelevance = rest.filter((item) => parseInt(item.dataset.relevance ?? "0", 10) <= threshold).sort(sortByQtyDesc)

    const lowRelevance = rest.filter((item) => parseInt(item.dataset.relevance ?? "0", 10) > threshold).sort(sortByQtyDesc)

    ;[...exactMatches.sort(sortByQtyDesc), ...highRelevance, ...lowRelevance].forEach((item) => grid.appendChild(item))
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches ? this.querySelector('[data-grid-layout="desktop-default-option"]') : this.querySelector('[data-grid-layout="mobile-option"]')

    if (!(targetElement instanceof HTMLInputElement)) return

    targetElement.checked = true
    this.#setLayout("default")
  }
}

if (!customElements.get("results-list")) {
  customElements.define("results-list", ResultsList)
}
